import React, { useLayoutEffect, useRef, useState } from 'react';
import I18n from 'modules/i18n';
import { LinkButton } from '@/components/ui/Buttons';
import {
  bottomRowOffsets,
  computeTruncation,
  EXPANDED_MAX_HEIGHT,
  EXPANDED_SHOW_LESS_SPACE,
  FIT_TOLERANCE,
  offsetRanges,
  setClippedRowsInert,
  TRUNCATE_CHECKED_ATTR,
  TRUNCATE_CHILDREN_ATTR,
  TRUNCATE_ROW_ATTR,
  Truncation,
} from './truncationUtils';

interface TruncatedCorrelationsListProps {
  children: React.ReactNode;
  collapsedMaxHeight: number;
  expanded: boolean;
  remeasureKey?: unknown;
  onToggle: (expanded: boolean) => void;
}

const TruncatedCorrelationsList = ({
  children, collapsedMaxHeight, expanded, remeasureKey, onToggle,
}: TruncatedCorrelationsListProps) => {
  const listContentRef = useRef<HTMLDivElement>(null);
  const clipContainerRef = useRef<HTMLDivElement>(null);
  const [truncation, setTruncation] = useState<Truncation | null>(null);
  const [expandedMaxHeight, setExpandedMaxHeight] = useState(EXPANDED_MAX_HEIGHT);
  const evaluatedAutoOpen = useRef(false);

  useLayoutEffect(() => {
    const remeasureTruncation = () => {
      const listContent = listContentRef.current;
      if (!listContent) return;

      const next = computeTruncation(
        bottomRowOffsets(listContent, TRUNCATE_ROW_ATTR),
        offsetRanges(listContent, TRUNCATE_CHILDREN_ATTR),
        collapsedMaxHeight,
        expandedMaxHeight,
      );

      setClippedRowsInert(listContent, expanded ? null : next?.clipHeight ?? null);

      setTruncation(prev => (
        (prev?.clipHeight === next?.clipHeight && prev?.hiddenCount === next?.hiddenCount) ? prev : next
      ));
    };

    remeasureTruncation();

    const observer = new ResizeObserver(remeasureTruncation);
    if (listContentRef.current) observer.observe(listContentRef.current);

    return () => observer.disconnect();
  }, [collapsedMaxHeight, expanded, expandedMaxHeight, remeasureKey]);

  useLayoutEffect(() => {
    if (!expanded && clipContainerRef.current) clipContainerRef.current.scrollTop = 0;
  }, [expanded]);

  // Cap the expanded height to the space under the section so "Show less" stays reachable.
  useLayoutEffect(() => {
    const remeasureExpandedMaxHeight = () => {
      const clipContainer = clipContainerRef.current;
      if (!clipContainer) return;

      const availableWindowSpace = window.innerHeight
        - clipContainer.getBoundingClientRect().top
        - EXPANDED_SHOW_LESS_SPACE;
      setExpandedMaxHeight(Math.max(Math.min(EXPANDED_MAX_HEIGHT, availableWindowSpace), collapsedMaxHeight));
    };

    remeasureExpandedMaxHeight();
    window.addEventListener('resize', remeasureExpandedMaxHeight);

    return () => window.removeEventListener('resize', remeasureExpandedMaxHeight);
  }, [collapsedMaxHeight, expanded, remeasureKey]);

  useLayoutEffect(() => {
    if (evaluatedAutoOpen.current || !truncation) return;

    // Mounting already-expanded settles the evaluation too — otherwise it would fire on the next
    // "Show less" click and reopen the section under the user.
    evaluatedAutoOpen.current = true;
    if (expanded) return;

    const listContent = listContentRef.current;
    if (!listContent) return;

    const checkedRowHidden = bottomRowOffsets(listContent, TRUNCATE_CHECKED_ATTR)
      .some(bottom => bottom > truncation.clipHeight + FIT_TOLERANCE);

    if (checkedRowHidden) onToggle(true);
  }, [expanded, truncation, onToggle]);

  const collapsedStyle = truncation ? { height: truncation.clipHeight } : undefined;
  const style = expanded
    ? { maxHeight: expandedMaxHeight }
    : collapsedStyle;

  return (
    <>
      <div
        ref={clipContainerRef}
        className={expanded ? 'tw-overflow-y-auto' : 'tw-overflow-hidden'}
        style={style}
      >
        <div ref={listContentRef}>{children}</div>
      </div>
      {truncation && (
        <LinkButton
          aria-expanded={expanded}
          className="tw-p-0 tw-mt-1 tw-text-sm"
          onClick={() => onToggle(!expanded)}
        >
          {expanded ? I18n.t('show_less') : I18n.t('show_count_more', { count: truncation.hiddenCount })}
        </LinkButton>
      )}
    </>
  );
};

export default TruncatedCorrelationsList;
